/**
 * Blok bot: greedy placement with look-ahead over the pieces still in the tray.
 *
 *   value of a placement sequence = sum over steps of (LINE_W * lines cleared + REVEAL_W * newly
 *   revealed picture blocks) + eval(final board)
 *   eval(board) = - FILL_W * filled cells - HOLE_W * enclosed holes - EDGE_W * filled/empty edges
 *
 * i.e. "maximise lines cleared, then minimise holes" (lines dominate; reveals break ties between
 * equal clears). All orders of the tray pieces and the best BEAM placements per piece are tried,
 * the first move of the best sequence is played, then the bot re-plans.
 *
 * `gradeLevel` runs the bot (one pure run + noisy "human-like" runs) for SURVIVE_PIECES pieces and reports
 * pieces-needed-for-40%-reveal and survival; survival is the level's difficulty band.
 */
import type { Picture } from "@enjoyment/core";
import { Rng, hashSeed } from "@enjoyment/core";
import { SIDE, piece } from "./pieces";
import { CELLS, createGame, placePiece, revealedFraction, type BlokLevel, type BlokState } from "./game";
import { FULL_ROW, boardToMasks, fits, filledCount, fullLines, place, popcount, type Masks } from "./masks";

const LINE_W = 100;
const REVEAL_W = 6;
const FILL_W = 2.2;
const HOLE_W = 14;
const EDGE_W = 1.2;
const DEAD_W = 5000;
const BEAM = 5;

export interface BotMove {
  slot: number;
  r: number;
  c: number;
  value: number;
}

/** Enclosed-hole penalty: small empty pockets (size 1: 4, 2: 2, 3: 1), found by bitmask flood fill. */
export function holePenalty(m: Masks): number {
  const empty: number[] = new Array<number>(SIDE);
  for (let r = 0; r < SIDE; r++) empty[r] = ~m[r]! & FULL_ROW;
  const region: number[] = new Array<number>(SIDE);
  let pen = 0;
  for (let r = 0; r < SIDE; r++) {
    while (empty[r]) {
      const seed = empty[r]! & -empty[r]!;
      region.fill(0);
      region[r] = seed;
      let changed = true;
      while (changed) {
        changed = false;
        for (let k = 0; k < SIDE; k++) {
          const v = region[k]!;
          let nv = v | (v << 1) | (v >> 1);
          if (k > 0) nv |= region[k - 1]!;
          if (k < SIDE - 1) nv |= region[k + 1]!;
          nv &= empty[k]!;
          if (nv !== v) {
            region[k] = nv;
            changed = true;
          }
        }
      }
      let size = 0;
      for (let k = 0; k < SIDE; k++) {
        size += popcount(region[k]!);
        empty[k] = empty[k]! & ~region[k]!;
      }
      if (size <= 3) pen += 4 / size;
    }
  }
  return pen;
}

function edges(m: Masks): number {
  let e = 0;
  for (let r = 0; r < SIDE; r++) {
    e += popcount((m[r]! ^ (m[r]! >> 1)) & (FULL_ROW >> 1));
    if (r < SIDE - 1) e += popcount(m[r]! ^ m[r + 1]!);
  }
  return e;
}

export function evalBoard(m: Masks): number {
  return -FILL_W * filledCount(m) - HOLE_W * holePenalty(m) - EDGE_W * edges(m);
}

interface Plan {
  slot: number;
  r: number;
  c: number;
  value: number;
}

/** Clear full lines on masks, returning (lines, newly revealed count) and updating `rev`. */
function clearTracked(m: Masks, rev: Masks): { lines: number; fresh: number } {
  const { rows, cols } = fullLines(m);
  if (!rows.length && !cols.length) return { lines: 0, fresh: 0 };
  let colMask = 0;
  for (const c of cols) colMask |= 1 << c;
  let fresh = 0;
  for (let r = 0; r < SIDE; r++) {
    const gone = rows.includes(r) ? FULL_ROW : colMask;
    fresh += popcount(gone & ~rev[r]!);
    rev[r] = rev[r]! | gone;
    m[r] = m[r]! & ~gone;
  }
  return { lines: rows.length + cols.length, fresh };
}

function search(m: Masks, rev: Masks, left: { slot: number; id: string }[], firstMoves: Plan[] | null, ev: number): number {
  if (!left.length) return ev;
  let best = -Infinity;
  const tried = new Set<string>();
  const cands: { i: number; r: number; c: number; v: number; ev: number; m: Masks; rev: Masks }[] = [];
  for (let i = 0; i < left.length; i++) {
    const { id } = left[i]!;
    if (tried.has(id)) continue;
    tried.add(id);
    const p = piece(id);
    for (let r = 0; r + p.h <= SIDE; r++)
      for (let c = 0; c + p.w <= SIDE; c++) {
        if (!fits(m, p, r, c)) continue;
        const nm = m.slice();
        const nrev = rev.slice();
        place(nm, p, r, c);
        const { lines, fresh } = clearTracked(nm, nrev);
        const e = evalBoard(nm);
        cands.push({ i, r, c, v: LINE_W * lines + REVEAL_W * fresh + e, ev: e, m: nm, rev: nrev });
      }
  }
  if (!cands.length) return -DEAD_W * left.length + ev;
  cands.sort((a, b) => b.v - a.v);
  // Beam per piece type so one piece cannot crowd the others out.
  const perPiece = new Map<number, number>();
  for (const cd of cands) {
    const used = perPiece.get(cd.i) ?? 0;
    if (used >= BEAM && !firstMoves) continue;
    if (used >= BEAM * 3 && firstMoves) continue;
    perPiece.set(cd.i, used + 1);
    const rest = left.filter((_, k) => k !== cd.i);
    const gain = cd.v - cd.ev;
    const total = gain + search(cd.m, cd.rev, rest, null, cd.ev);
    if (total > best) best = total;
    firstMoves?.push({ slot: left[cd.i]!.slot, r: cd.r, c: cd.c, value: total });
  }
  return best;
}

function revealMasks(s: BlokState): Masks {
  const rev: Masks = new Array<number>(SIDE).fill(0);
  s.revealed.forEach((v, i) => {
    if (v) rev[Math.floor(i / SIDE)] = rev[Math.floor(i / SIDE)]! | (1 << (i % SIDE));
  });
  return rev;
}

/** All first moves of the best plans, best first (deduplicated per slot/position). */
export function rankMoves(s: BlokState): BotMove[] {
  const left = s.tray.map((id, slot) => ({ slot, id })).filter((x): x is { slot: number; id: string } => x.id !== null);
  const first: Plan[] = [];
  const m0 = boardToMasks(s.board);
  search(m0, revealMasks(s), left, first, evalBoard(m0));
  const best = new Map<string, BotMove>();
  for (const f of first) {
    const k = `${f.slot}:${f.r}:${f.c}`;
    const prev = best.get(k);
    if (!prev || f.value > prev.value) best.set(k, f);
  }
  return [...best.values()].sort((a, b) => b.value - a.value || a.slot - b.slot || a.r - b.r || a.c - b.c);
}

export function chooseMove(s: BlokState): BotMove | null {
  return rankMoves(s)[0] ?? null;
}

export interface BotRun {
  placed: number;
  died: boolean;
  /** Pieces placed when reveal first reached the target (null = never). */
  piecesToTarget: number | null;
  reveal: number;
  score: number;
}

export interface BotRunOptions {
  /** Stop after reaching this revealed fraction (default 0.4). */
  target?: number;
  /** Chance per move of playing one of the top-4 plans at random instead of the best (a human-like slip). */
  noise?: number;
  seed?: number;
  maxPieces?: number;
  /** Keep playing after the target (survival tests). */
  playOn?: boolean;
}

export function runBot(level: BlokLevel, picture: Picture, opts: BotRunOptions = {}): BotRun {
  const target = opts.target ?? 0.4;
  const max = opts.maxPieces ?? 200;
  const rng = new Rng(hashSeed(`bot:${level.seed}:${opts.seed ?? 0}`));
  const s = createGame(level, picture);
  let hit: number | null = null;
  while (s.status === "playing" && s.placed < max) {
    const ranked = rankMoves(s);
    if (!ranked.length) break;
    const mv = opts.noise && rng.next() < opts.noise ? ranked[rng.int(Math.min(4, ranked.length))]! : ranked[0]!;
    placePiece(s, mv.slot, mv.r, mv.c);
    if (hit === null && revealedFraction(s) >= target) {
      hit = s.placed;
      if (!opts.playOn) break;
    }
  }
  return { placed: s.placed, died: s.status === "over", piecesToTarget: hit, reveal: revealedFraction(s), score: s.score };
}

export type Band = "easy" | "medium" | "hard" | "expert" | "broken";

export function bandOf(survival: number): Band {
  return survival >= 0.95 ? "easy" : survival >= 0.8 ? "medium" : survival >= 0.5 ? "hard" : survival >= 0.3 ? "expert" : "broken";
}

/** A level is "survived" when the bot is still alive after this many pieces (plenty to solve a picture). */
export const SURVIVE_PIECES = 30;

export interface Grade {
  band: Band;
  /** Fraction of runs that reached 40% revealed AND were still alive after SURVIVE_PIECES pieces. */
  survival: number;
  /** Median pieces needed to reach 40% over the runs that got there (NaN if none). */
  piecesTo40: number;
  /** Pieces the pure greedy run survived (capped at SURVIVE_PIECES). */
  greedySurvived: number;
}

/** Trial 0 = pure greedy; the others slip with 25% probability (a decent human). */
export function gradeLevel(level: BlokLevel, picture: Picture, trials = 6): Grade {
  const runs: BotRun[] = [];
  for (let t = 0; t < trials; t++) runs.push(runBot(level, picture, { noise: t === 0 ? 0 : 0.25, seed: t, playOn: true, maxPieces: SURVIVE_PIECES }));
  const reached = runs.filter((r) => r.piecesToTarget !== null);
  const need = reached.map((r) => r.piecesToTarget!).sort((a, b) => a - b);
  const alive = reached.filter((r) => !r.died && r.placed >= SURVIVE_PIECES).length;
  return {
    band: bandOf(alive / trials),
    survival: alive / trials,
    piecesTo40: need.length ? need[Math.floor(need.length / 2)]! : NaN,
    greedySurvived: runs[0]!.placed,
  };
}
